/**
 * useHomeGroup Hook
 *
 * Fetches a single home group by ID using URQL.
 */

import { useQuery } from "urql";

// =============================================================================
// QUERY DOCUMENT
// =============================================================================

const HomeGroupDocument = `
  query HomeGroup($id: ID!) {
    homeGroup(id: $id) {
      id
      name
      description
      isOpen
      isMember
      memberCount
      imageUrl
      leader {
        id
        firstName
        lastName
        username
      }
      coLeader {
        id
        firstName
        lastName
        username
      }
      schedules {
        id
        dayOfWeek
        startTime
        endTime
        isActive
      }
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface HomeGroupDetail {
  id: string;
  name: string;
  description: string | null;
  isOpen: boolean;
  isMember: boolean;
  memberCount: number;
  imageUrl: string | null;
  leader: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    username: string | null;
  };
  coLeader: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    username: string | null;
  } | null;
  schedules: Array<{
    id: string;
    dayOfWeek: string;
    startTime: string;
    endTime: string | null;
    isActive: boolean;
  }>;
}

interface UseHomeGroupOptions {
  pause?: boolean;
}

interface UseHomeGroupResult {
  homeGroup: HomeGroupDetail | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Fetch a single home group by ID.
 */
export function useHomeGroup(
  id: string | undefined,
  options: UseHomeGroupOptions = {}
): UseHomeGroupResult {
  const { pause = false } = options;

  const [result, reexecuteQuery] = useQuery<{
    homeGroup: HomeGroupDetail | null;
  }>({
    query: HomeGroupDocument,
    variables: { id },
    pause: pause || !id,
  });

  return {
    homeGroup: result.data?.homeGroup ?? null,
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}
